// When should a video banner be on screen? Shared by both frontends so this — the part most likely to
// drift — exists once. The renderers differ; this does not.

import { on } from 'shared/javascripts/util/events'

export interface BannerTiming {
  appears_at: number
  duration: number
}

export interface BannerVisibility {
  /** The playhead is inside the banner's time window. */
  inTimeWindow: boolean
  adRunning: boolean
  closed: boolean
}

export interface WatchedBanner {
  /** Dismiss the banner for the rest of the video. */
  close: () => void
  destroy: () => void
}

/** True when all three flags together mean "show it". */
export function bannerShown({ inTimeWindow, adRunning, closed }: BannerVisibility): boolean {
  return inTimeWindow && !adRunning && !closed
}

/**
 * Tracks one banner's visibility from the player's events on `container` (the element the player emits
 * `app:player:*` on). `onChange` fires only when the state actually changes — `app:player:time` arrives
 * several times a second.
 */
export function watchBanner(
  container: HTMLElement,
  timing: BannerTiming,
  onChange: (visibility: BannerVisibility) => void,
): WatchedBanner {
  const startsAt = timing.appears_at
  const endsAt = timing.appears_at + timing.duration

  const state: BannerVisibility = { inTimeWindow: false, adRunning: false, closed: false }

  const update = (changes: Partial<BannerVisibility>): void => {
    const changed = Object.entries(changes).some(
      ([key, value]) => state[key as keyof BannerVisibility] !== value,
    )
    if (!changed) {
      return
    }
    Object.assign(state, changes)
    onChange({ ...state })
  }

  const unsubscribes = [
    on(container, 'app:player:time', (event: Event & { time: number }) => {
      update({ inTimeWindow: startsAt <= event.time && event.time <= endsAt })
    }),
    on(container, 'app:player:adStarted', () => update({ adRunning: true })),
    on(container, 'app:player:adFinished', () => update({ adRunning: false })),
  ]

  return {
    close() {
      update({ closed: true })
    },

    destroy() {
      for (const unsubscribe of unsubscribes) {
        unsubscribe()
      }
    },
  }
}
